import { useState, useEffect, useCallback, useRef, Fragment } from "react";
import { t } from "../vocab";
import api from "../api";
import { FileText, Send, CheckCircle2, XCircle, Paperclip, Clock, Wallet, CheckCheck } from "lucide-react";
import AnexosBox from "../components/AnexosBox";
import { podeVerValores } from "../components/Permissoes";

const fmt = (v) => `R$${Number(v||0).toLocaleString("pt-BR",{minimumFractionDigits:2,maximumFractionDigits:2})}`;
function fmtDataBR(d){ if(!d) return "—"; const s=String(d); const m=s.match(/^(\d{4})-(\d{2})-(\d{2})/); if(m) return `${m[3]}/${m[2]}/${m[1]}`; return "—"; }
function toInput(d){ if(!d) return ""; const s=String(d); const m=s.match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[1]}-${m[2]}-${m[3]}` : ""; }

const ST = {
  rascunho: { t:"Rascunho", c:"#98A2B3" },
  enviado:  { t:"Enviado",  c:"#579bfc" },
  aprovado: { t:"Aprovado", c:"#1D9E75" },
  recusado: { t:"Recusado", c:"#E24B4A" },
  expirado: { t:"Expirado", c:"#EBA13A" },
};

const ICONE_ST = { rascunho:Clock, enviado:Send, aprovado:CheckCircle2, recusado:Wallet, expirado:CheckCheck };

const PRI = {
  baixa: { t:"Baixa", c:"#98A2B3" },
  media: { t:"Média", c:"#EBA13A" },
  alta:  { t:"Alta",  c:"#E24B4A" },
};

const th = { padding:"10px 12px", textAlign:"left", fontSize:11, fontWeight:700, color:"var(--text-secondary)", textTransform:"uppercase", letterSpacing:.4, borderBottom:"1px solid var(--border-color)", whiteSpace:"nowrap" };
const td = { padding:"10px 12px", fontSize:13, color:"var(--text-secondary)", borderBottom:"1px solid var(--border-light)", verticalAlign:"middle" };
const inp = { width:"100%", padding:"8px 10px", border:"1px solid var(--border-color)", borderRadius:6, fontSize:13, outline:"none", boxSizing:"border-box" };
const lbl = { display:"block", fontSize:11, fontWeight:700, color:"var(--text-secondary)", textTransform:"uppercase", letterSpacing:.4, marginBottom:4 };

function Badge({ s }) {
  const o = ST[s] || ST.rascunho;
  return <span style={{ display:"inline-block", padding:"3px 10px", borderRadius:12, fontSize:11, fontWeight:600, color:o.c, background:o.c+"1A", border:`1px solid ${o.c}40` }}>{o.t}</span>;
}

function Combo({ opcoes, valor, onChange, placeholder = "Buscar...", vazio = "Nenhum" }) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState("");
  const ref = useRef(null);

  const sel = opcoes.find(o => o.id === valor);

  useEffect(() => {
    const fora = (e) => { if (ref.current && !ref.current.contains(e.target)) { setAberto(false); setBusca(""); } };
    document.addEventListener("mousedown", fora);
    return () => document.removeEventListener("mousedown", fora);
  }, []);

  const filtradas = busca
    ? opcoes.filter(o => o.label.toLowerCase().includes(busca.toLowerCase()))
    : opcoes;

  const escolher = (id) => { onChange(id); setAberto(false); setBusca(""); };

  return (
    <div ref={ref} style={{ position:"relative" }}>
      <input
        style={{ ...inp, cursor:"text", background:"var(--card-bg)" }}
        value={aberto ? busca : (sel ? sel.label : "")}
        placeholder={sel ? sel.label : placeholder}
        onFocus={() => { setAberto(true); setBusca(""); }}
        onChange={e => { setBusca(e.target.value); if (!aberto) setAberto(true); }}
      />
      <span style={{ position:"absolute", right:10, top:9, color:"#98A2B3", fontSize:11, pointerEvents:"none" }}>▾</span>
      {aberto && (
        <div style={{ position:"absolute", top:"100%", left:0, right:0, marginTop:4, background:"var(--card-bg)", border:"1px solid var(--border-color)", borderRadius:6, boxShadow:"0 8px 24px rgba(16,24,40,.12)", maxHeight:220, overflowY:"auto", zIndex:50 }}>
          <div onClick={() => escolher("")} style={{ padding:"8px 10px", fontSize:13, color:"#98A2B3", cursor:"pointer", borderBottom:"1px solid var(--border-light)" }}>{vazio}</div>
          {filtradas.map(o => (
            <div key={o.id} onClick={() => escolher(o.id)}
              onMouseEnter={e => e.currentTarget.style.background = "var(--page-bg)"}
              onMouseLeave={e => e.currentTarget.style.background = "var(--card-bg)"}
              style={{ padding:"8px 10px", fontSize:13, cursor:"pointer", background:"var(--card-bg)", color: o.id === valor ? "#EE6A1A" : "var(--text-secondary)", fontWeight: o.id === valor ? 700 : 400 }}>
              {o.label}
            </div>
          ))}
          {!filtradas.length && (
            <div style={{ padding:"10px", fontSize:12, color:"#98A2B3", textAlign:"center" }}>Nada encontrado</div>
          )}
        </div>
      )}
    </div>
  );
}

function Btn({ children, onClick, cor="var(--text-secondary)", full }) {
  return <button onClick={onClick} style={{ padding:"6px 12px", fontSize:12, fontWeight:600, color:full?"var(--card-bg)":cor, background:full?cor:"transparent", border:`1px solid ${cor}${full?"":"66"}`, borderRadius:6, cursor:"pointer", whiteSpace:"nowrap" }}>{children}</button>;
}

function Card({ n, label, cor, Icon, count, ativo, onClick }) {
  return (
    <div onClick={onClick} style={{ flex:1, minWidth:170, padding:"12px 14px", borderRadius:12, background:"var(--card-bg)", border:`1px solid ${ativo?cor:"var(--border-color)"}`, boxShadow: ativo ? `0 0 0 1px ${cor}` : "none", cursor:onClick?"pointer":"default", display:"flex", alignItems:"center", gap:12 }}>
      <div style={{ width:38, height:38, borderRadius:10, background:cor+"18", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
        {Icon && <Icon size={19} color={cor} />}
      </div>
      <div style={{ minWidth:0 }}>
        <div style={{ fontSize:18, fontWeight:800, color:"var(--text-primary)", lineHeight:1.15, whiteSpace:"nowrap" }}>{n}</div>
        <div style={{ fontSize:11, fontWeight:600, color:"var(--text-secondary)", marginTop:2, whiteSpace:"nowrap" }}>{label}{count!=null ? " · " + count : ""}</div>
      </div>
    </div>
  );
}

function carregarScript(src) {
  return new Promise((res, rej) => {
    if (document.querySelector(`script[src="${src}"]`)) return res();
    const s = document.createElement("script");
    s.src = src; s.onload = res; s.onerror = rej;
    document.body.appendChild(s);
  });
}

function imgParaDataURL(url) {
  return new Promise((res) => {
    if (!url) return res(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = img.naturalWidth; c.height = img.naturalHeight;
        c.getContext("2d").drawImage(img, 0, 0);
        res({ data: c.toDataURL("image/png"), w: img.naturalWidth, h: img.naturalHeight });
      } catch (e) { res(null); }
    };
    img.onerror = () => res(null);
    img.src = url;
  });
}

export default function Orcamentos({ currentUser, prefill, onPrefillUsado }) {
  const verValores = podeVerValores(currentUser && currentUser.role);
  const [lista, setLista] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [negocs, setNegocs] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [empresa, setEmpresa] = useState({});
  const [usuarios, setUsuarios] = useState([]);
  const [filtro, setFiltro] = useState("todos");
  const _agora = new Date();
  const [mesRef, setMesRef] = useState({ ano: _agora.getFullYear(), mes: _agora.getMonth() });
  const [verTodos, setVerTodos] = useState(false);
  const [verArquivados, setVerArquivados] = useState(false);
  const [buscaOrc, setBuscaOrc] = useState("");
  const [modal, setModal] = useState(null);
  const [view, setView] = useState(() => { try { return localStorage.getItem("orc_view") || "lista"; } catch(e){ return "lista"; } });
  const [load, setLoad] = useState(false);

  useEffect(() => {
    if (prefill && clientes.length) {
      const cli = clientes.find(c => (c.nomeEmpresa||c.nomeContato||"").trim().toLowerCase() === (prefill.nomeCliente||"").trim().toLowerCase());
      const its = (Number(prefill.valor)>0) ? [{ tipo:"servico", descricao: prefill.servico || prefill.titulo || "Servico", ncm:"", unidade:"un", quantidade:1, valorUnitario:Number(prefill.valor)||0, desconto:0 }] : [];
      setModal({ novo:true, status:"rascunho", dataEmissao:new Date().toISOString().slice(0,10), titulo:prefill.titulo||"", nomeCliente:prefill.nomeCliente||"", cliente: cli?cli._id:"", responsavel:prefill.responsavel||"", nomeResponsavel:prefill.nomeResponsavel||"", negociacao:prefill.negociacao||null, itens: its });
      onPrefillUsado && onPrefillUsado();
    }
  }, [prefill, clientes]);

  const carregar = useCallback(async () => {
    setLoad(true);
    try {
      const [rO, rC, rN, rCat, rE, rU] = await Promise.all([
        api.get("/orcamentos"),
        api.get("/clientes"),
        api.get("/leads?limit=1000&tipo=negociacao"),
        api.get("/catalogo"),
        api.get("/empresa"),
        api.get("/users"),
      ]);
      setLista(Array.isArray(rO.data) ? rO.data : (rO.data?.itens || []));
      setClientes(Array.isArray(rC.data) ? rC.data : (rC.data?.clientes || rC.data?.itens || []));
      setNegocs(Array.isArray(rN.data) ? rN.data : (rN.data?.leads || rN.data?.itens || []));
      setCatalogo(Array.isArray(rCat.data) ? rCat.data : []);
      setEmpresa(rE.data || {});
      setUsuarios(Array.isArray(rU.data) ? rU.data : (rU.data?.users || rU.data?.itens || []));
    } catch (e) { console.error("Erro ao carregar orcamentos", e); }
    setLoad(false);
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const recarregarCatalogo = useCallback(async () => {
    try {
      const r = await api.get("/catalogo");
      setCatalogo(Array.isArray(r.data) ? r.data : []);
    } catch (e) { console.error("Erro ao recarregar catalogo", e); }
  }, []);

  const base = verArquivados ? lista.filter(o => o.arquivado) : lista.filter(o => !o.arquivado);
  const arquivar = async (id, val) => { try { await api.put("/orcamentos/" + id, { arquivado: val }); setLista(prev => prev.map(o => o._id === id ? { ...o, arquivado: val } : o)); } catch(e){ alert("Erro ao arquivar"); } };
  const clonar = async (o) => {
    const padrao = new Date().toISOString().slice(0,10);
    const alvo = window.prompt("Clonar " + (o.numero||"orcamento") + " para qual data de emissão? (AAAA-MM-DD)\n\nO clone recebe um número novo na sequência e entra como rascunho.", padrao);
    if (alvo === null) return;
    try {
      const { data } = await api.post("/orcamentos/" + o._id + "/clonar", { dataEmissao: alvo });
      alert("Clonado como " + (data.numero || "novo orçamento") + ".");
      carregar();
    } catch(e) { alert("Erro ao clonar: " + (e.response?.data?.erro || e.message)); }
  };
  const visiveis = filtro === "todos" ? base : base.filter(o => o.status === filtro);
  const refMes = (o) => (["aprovado","recusado","expirado"].includes(o.status) ? (o.dataAprovacao || o.dataEmissao || o.createdAt) : (o.dataEmissao || o.createdAt));
  const noPeriodo = (o) => { const ref = refMes(o); if (!ref) return false; if (verTodos) return true; const d = new Date(ref); return d.getMonth() === mesRef.mes && d.getFullYear() === mesRef.ano; };
  const listaPer = verArquivados ? base : base.filter(noPeriodo);
  const matchBusca = (o) => { const q = buscaOrc.trim().toLowerCase(); if (!q) return true; return [o.numero, o.nomeCliente, o.titulo, fmtDataBR(o.dataEmissao), fmtDataBR(o.validade), o.total, Number(o.total||0).toLocaleString("pt-BR"), Number(o.total||0).toLocaleString("pt-BR",{minimumFractionDigits:2})].some(x => String(x||"").toLowerCase().includes(q)); };
  const listaKanban = base.filter(o => o.status === "expirado" ? noPeriodo(o) : true);
  const baseCards = buscaOrc.trim() ? base.filter(matchBusca) : (view === "kanban" ? listaKanban.filter(noPeriodo) : listaPer);
  const visLista = buscaOrc.trim() ? base.filter(matchBusca) : (filtro === "todos" ? listaPer : listaPer.filter(o => o.status === filtro));
  const gruposMes = [];
  visLista.forEach(o => { const d = new Date(refMes(o)); const chave = d.getFullYear() + "-" + String(d.getMonth()+1).padStart(2,"0"); let g = gruposMes.find(x => x.chave === chave); if (!g) { g = { chave, data: d, itens: [] }; gruposMes.push(g); } g.itens.push(o); });
  gruposMes.sort((a,b) => b.chave.localeCompare(a.chave));

  const gerarRelatorioPDF = async () => {
    try {
      await carregarScript("/vendor/jspdf.umd.min.js");
      await carregarScript("/vendor/jspdf.plugin.autotable.min.js");
      const { jsPDF } = window.jspdf;
      const doc = new jsPDF({ orientation: "landscape" });
      try { const _r = await fetch(((empresa && empresa.logo) || "/logo-optimus.png")); const _b = await _r.blob(); const _logo = await new Promise(res=>{ const fr=new FileReader(); fr.onload=()=>res(fr.result); fr.readAsDataURL(_b); }); doc.addImage(_logo, "PNG", doc.internal.pageSize.getWidth()-14-26, 5, 26, 25.7); } catch(e){}
      const E = empresa || {};
      const hexRgb = (h) => { const m = String(h||"#888888").replace("#","").match(/.{2}/g); return m ? m.map(x => parseInt(x,16)) : [136,136,136]; };
      const per = verTodos ? "Todos os periodos" : new Date(mesRef.ano, mesRef.mes, 1).toLocaleDateString("pt-BR", { month:"long", year:"numeric" });
      doc.setFontSize(15); doc.setFont(undefined, "bold");
      doc.text(String(E.nome || ""), 14, 15);
      doc.setFontSize(11); doc.text("Relatorio de Orcamentos", 14, 22);
      doc.setFontSize(9); doc.setFont(undefined, "normal"); doc.setTextColor(120);
      doc.text("Periodo: " + per.charAt(0).toUpperCase() + per.slice(1), 14, 28);
      doc.setTextColor(0);
      let y = 33; let totalGeral = 0; let qtdGeral = 0;
      Object.keys(ST).forEach(st => {
        const arr = listaPer.filter(o => o.status === st);
        if (!arr.length) return;
        const sub = arr.reduce((a,o) => a + Number(o.total||0), 0);
        totalGeral += sub; qtdGeral += arr.length;
        const body = arr.map(o => [o.numero||"-", o.nomeCliente||"-", o.titulo||"-", o.nomeResponsavel||"-", fmtDataBR(o.validade), fmt(o.total)]);
        const HC = { fillColor: hexRgb(ST[st].c), textColor:[255,255,255], fontStyle:"bold" };
        const SC = { fillColor:[245,245,245], textColor:[80,80,80] };
        doc.autoTable({
          startY: y,
          head: [
            [{ content: ST[st].t + "  (" + arr.length + ")", colSpan: 5, styles: { halign:"left", ...HC } }, { content: fmt(sub), styles: { halign:"right", ...HC } }],
            [{ content:"Numero", styles:SC }, { content:"Cliente", styles:SC }, { content:"Assunto", styles:SC }, { content:"Vendedor", styles:SC }, { content:"Validade", styles:SC }, { content:"Valor", styles:{...SC, halign:"right"} }]
          ],
          body,
          styles: { fontSize: 8.5, cellPadding: 2 },
          columnStyles: { 0:{cellWidth:30}, 1:{cellWidth:55}, 2:{cellWidth:90}, 3:{cellWidth:45}, 4:{cellWidth:25}, 5:{cellWidth:24, halign:"right"} },
          margin: { left:14, right:14 },
        });
        y = doc.lastAutoTable.finalY + 5;
      });
      const _H = doc.internal.pageSize.getHeight();
      if (y + 14 > _H) { doc.addPage(); y = 20; }
      const _W = doc.internal.pageSize.getWidth();
      doc.setFillColor(238,106,26); doc.roundedRect(14, y, _W-28, 11, 2, 2, "F");
      doc.setTextColor(255,255,255); doc.setFont(undefined,"bold"); doc.setFontSize(11);
      doc.text("TOTAL GERAL  (" + qtdGeral + " orcamentos)", 18, y + 7);
      doc.text(fmt(totalGeral), _W-18, y + 7, { align:"right" });
      doc.setTextColor(0);
      doc.save("Relatorio-Orcamentos-" + (verTodos ? "todos" : (mesRef.ano + "-" + String(mesRef.mes+1).padStart(2,"0"))) + ".pdf");
    } catch (e) { alert("Erro ao gerar relatorio: " + e.message); }
  };
  const somaPor = (s) => baseCards.filter(o => o.status === s).reduce((a, o) => a + Number(o.total || 0), 0);

  const excluir = async (id) => {
    if (!window.confirm("Excluir este orçamento?")) return;
    try { await api.delete(`/orcamentos/${id}`); carregar(); }
    catch (e) { alert("Erro ao excluir"); }
  };

  const mudarStatus = async (id, status) => {
    setLista(prev => prev.map(o => o._id === id ? { ...o, status } : o));
    try { await api.put(`/orcamentos/${id}`, { status }); }
    catch (e) { alert("Erro ao mudar status"); carregar(); }
  };

  const salvarCampo = async (id, campos) => {
    setLista(prev => prev.map(o => o._id === id ? { ...o, ...campos } : o));
    try { await api.put(`/orcamentos/${id}`, campos); }
    catch (e) { alert("Erro ao salvar"); carregar(); }
  };

  const renomearStatus = async (key, label) => {
    if (ST[key]) ST[key].t = label;
    const novo = { ...(empresa.statusOrcamento || {}), [key]: label };
    setEmpresa(e => ({ ...e, statusOrcamento: novo }));
    try { await api.put("/empresa", { statusOrcamento: novo }); } catch (e) {}
  };

  const renomearCor = async (key, cor) => {
    if (ST[key]) ST[key].c = cor;
    const novo = { ...(empresa.statusOrcamentoCor || {}), [key]: cor };
    setEmpresa(e => ({ ...e, statusOrcamentoCor: novo }));
    try { await api.put("/empresa", { statusOrcamentoCor: novo }); } catch (e) {}
  };

  const gerarPDF = async (o) => {
    try {
      await carregarScript("/vendor/jspdf.umd.min.js");
      await carregarScript("/vendor/jspdf.plugin.autotable.min.js");
      const { jsPDF } = window.jspdf;
      const doc = new jsPDF({ orientation: "landscape" });
      const E = empresa || {};
      const L = 14, R = 283, W = R - L;
      const cli = clientes.find(c => c._id === (o.cliente?._id || o.cliente)) || {};
      const ORANGE = [238, 106, 26], DARK = [26, 34, 43], GREEN = [29, 158, 117];

      // ===== CABECALHO (faixa laranja) =====
      doc.setFillColor(ORANGE[0], ORANGE[1], ORANGE[2]);
      doc.rect(0, 0, 297, 30, "F");

      // logo em badge branco
      const logo = await imgParaDataURL(E.logo || "");
      if (logo) {
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(L, 4, 22, 22, 2, 2, "F");
        const alt = 18, larg = Math.min(18, (logo.w / logo.h) * alt);
        try { doc.addImage(logo.data, "PNG", L + (22 - larg) / 2, 6, larg, alt); } catch (e) {}
      }
      const xTexto = logo ? L + 29 : L;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(15); doc.setTextColor(255, 255, 255);
      doc.text((E.nome || "SUA EMPRESA").toUpperCase(), xTexto, 14);
      if (E.slogan) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8.5); doc.setTextColor(255, 224, 200);
        doc.text(E.slogan, xTexto, 20);
      }

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8); doc.setTextColor(255, 224, 200);
      doc.text("PROPOSTA COMERCIAL", R, 12, { align: "right" });
      doc.setFont("helvetica", "bold");
      doc.setFontSize(15); doc.setTextColor(255, 255, 255);
      doc.text(o.numero || "-", R, 20, { align: "right" });
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5); doc.setTextColor(255, 224, 200);
      doc.text("Emissão " + fmtDataBR(o.dataEmissao) + "   Validade " + fmtDataBR(o.validade), R, 26, { align: "right" });

      // ===== BARRA DE CONTATOS =====
      doc.setFillColor(250, 248, 246);
      doc.rect(0, 30, 297, 8, "F");
      doc.setFontSize(8); doc.setTextColor(120, 120, 120);
      const contatos = [
        E.cnpj && ("CNPJ " + E.cnpj),
        [E.endereco, E.cidade].filter(Boolean).join(" - "),
        E.telefone,
        E.site,
      ].filter(Boolean).join("     |     ");
      if (contatos) doc.text(contatos, 148.5, 35, { align: "center" });

      let y = 42;

      // ===== AREAS DE ATUACAO (pills) =====
      const areas = (E.areasAtuacao || "").split(/[;,]/).map(a => a.trim()).filter(Boolean);
      if (areas.length) {
        let x = L;
        doc.setFontSize(7.5);
        areas.forEach(a => {
          const wpill = doc.getTextWidth(a) + 8;
          if (x + wpill > R) return;
          doc.setFillColor(250, 236, 231);
          doc.roundedRect(x, y - 4, wpill, 6, 3, 3, "F");
          doc.setTextColor(153, 60, 29);
          doc.text(a, x + 4, y);
          x += wpill + 4;
        });
        y += 9;
      }

      // ===== CLIENTE + DETALHES (2 caixas) =====
      const boxH = 28, gap = 6, halfW = (W - gap) / 2;
      doc.setDrawColor(236, 238, 242);
      doc.roundedRect(L, y, halfW, boxH, 2, 2, "D");
      doc.roundedRect(L + halfW + gap, y, halfW, boxH, 2, 2, "D");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.5); doc.setTextColor(154, 161, 173);
      doc.text("CLIENTE", L + 5, y + 7);
      doc.setFontSize(13); doc.setTextColor(26, 34, 43);
      doc.text((o.nomeCliente || "-").slice(0, 44), L + 5, y + 15);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5); doc.setTextColor(107, 114, 128);
      let yc2 = y + 20.5;
      if (cli.cnpj) { doc.text("CNPJ: " + cli.cnpj, L + 5, yc2); yc2 += 4.5; }
      const endCli = [cli.endereco, cli.cidade].filter(Boolean).join(" - ");
      if (endCli) doc.text(endCli.slice(0, 62), L + 5, yc2);
      const dx = L + halfW + gap + 5;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.5); doc.setTextColor(154, 161, 173);
      doc.text("DETALHES", dx, y + 7);
      let yd = y + 15;
      if (o.titulo) {
        doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(154, 161, 173); doc.text("Assunto:", dx, yd);
        doc.setFont("helvetica", "normal"); doc.setTextColor(26, 34, 43); doc.text(String(o.titulo).slice(0, 40), dx + 18, yd);
        yd += 6.5;
      }
      doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(154, 161, 173); doc.text("Vendedor:", dx, yd);
      doc.setFont("helvetica", "normal"); doc.setTextColor(26, 34, 43); doc.text(o.nomeResponsavel || "-", dx + 20, yd);
      y += boxH + 4;

      // ===== TABELAS (Produtos / Servicos) =====
      const soma = (arr) => arr.reduce((s, i) => s + ((i.quantidade || 0) * (i.valorUnitario || 0) - (i.desconto || 0)), 0);
      const produtos = (o.itens || []).filter(i => i.tipo !== "servico");
      const servicos = (o.itens || []).filter(i => i.tipo === "servico");

      const desenharSecao = (titulo, arr, comNcm) => {
        if (!arr.length) return;
        doc.setFillColor(ORANGE[0], ORANGE[1], ORANGE[2]);
        doc.rect(L, y - 3, 2.5, 5, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10.5); doc.setTextColor(26, 34, 43);
        doc.text(titulo, L + 5, y + 1);
        y += 5;

        const usaNcm = comNcm && arr.some(i => i.ncm);
        const head = usaNcm
          ? ["#", "Descrição", "NCM", "Un.", "Qtd", "Unitário", "Desc.", "Total"]
          : ["#", "Descrição", "Un.", "Qtd", "Unitário", "Desc.", "Total"];
        const body = arr.map((i, n) => {
          const l = [n + 1, i.descricao];
          if (usaNcm) l.push(i.ncm || "-");
          l.push(i.unidade || "un", String(i.quantidade || 0), fmt(i.valorUnitario), fmt(i.desconto), fmt((i.quantidade || 0) * (i.valorUnitario || 0) - (i.desconto || 0)));
          return l;
        });
        const c = { 0: { cellWidth: 8, halign: "center" } };
        let k = 2;
        if (usaNcm) { c[2] = { cellWidth: 19, halign: "center" }; k = 3; }
        c[k] = { cellWidth: 11, halign: "center" };
        c[k + 1] = { cellWidth: 11, halign: "center" };
        c[k + 2] = { cellWidth: 22, halign: "right" };
        c[k + 3] = { cellWidth: 17, halign: "right" };
        c[k + 4] = { cellWidth: 24, halign: "right", fontStyle: "bold" };

        doc.autoTable({
          startY: y,
          head: [head],
          body,
          theme: "plain",
          tableWidth: W,
          margin: { left: L, right: 297 - R },
          styles: { overflow: "linebreak", valign: "middle", cellPadding: 1.6, fontSize: 8.5, textColor: [40, 48, 60], lineColor: [240, 241, 244], lineWidth: 0.1 },
          headStyles: { fontStyle: "bold", fontSize: 8, textColor: [26, 34, 43], lineColor: [26, 34, 43], lineWidth: { bottom: 0.5, top: 0, left: 0, right: 0 }, cellPadding: 2.6 },
          alternateRowStyles: { fillColor: [250, 251, 252] },
          columnStyles: c,
        });
        y = doc.lastAutoTable.finalY + 4;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.5); doc.setTextColor(ORANGE[0], ORANGE[1], ORANGE[2]);
        doc.text("Subtotal " + titulo + ": " + fmt(soma(arr)), R, y, { align: "right" });
        y += 3;
      };

      desenharSecao("Produtos", produtos, true);
      desenharSecao("Serviços", servicos, false);
      y += 2;

      // ===== TOTAIS + CONDICOES =====
      const subtotal = soma(produtos) + soma(servicos);
      const _Hp = doc.internal.pageSize.getHeight();
      if (y + 28 > _Hp - 24) { doc.addPage(); y = 20; }
      const condH = 18;
      const condW = W - 78;
      doc.setFillColor(246, 247, 249);
      doc.roundedRect(L, y, condW, condH, 2, 2, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.5); doc.setTextColor(154, 161, 173);
      doc.text("CONDIÇÕES", L + 4, y + 6);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5); doc.setTextColor(75, 85, 99);
      let yc = y + 12;
      if (o.condicoesPagamento) { doc.text("Pagamento: " + o.condicoesPagamento, L + 4, yc); yc += 5; }
      if (o.prazoEntrega) { doc.text("Entrega: " + o.prazoEntrega, L + 4, yc); yc += 5; }

      const tx = L + condW + 6, tw = 72;
      doc.setFontSize(8.5); doc.setTextColor(107, 114, 128);
      doc.text("Subtotal", tx, y + 4); doc.text(fmt(subtotal), tx + tw, y + 4, { align: "right" });
      if (Number(o.descontoGeral) > 0) {
        doc.setTextColor(153, 60, 29);
        doc.text("Desconto", tx, y + 9); doc.text("- " + fmt(o.descontoGeral), tx + tw, y + 9, { align: "right" });
      }
      doc.setFillColor(ORANGE[0], ORANGE[1], ORANGE[2]);
      doc.roundedRect(tx, y + 11, tw, 14, 2, 2, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9); doc.setTextColor(255, 255, 255);
      doc.text("VALOR TOTAL", tx + 5, y + 19.5);
      doc.setFontSize(18); doc.setTextColor(255, 255, 255);
      doc.text(fmt(o.total), tx + tw - 5, y + 20, { align: "right" });
      y += condH + 8;

      // ===== OBSERVACOES =====
      const obs = o.observacoes || E.observacoesPadrao || "";
      if (obs) {
        doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(154, 161, 173);
        doc.text("OBSERVAÇÕES", L, y); y += 4;
        doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(75, 85, 99);
        const linhasObs = doc.splitTextToSize(obs, W);
        doc.text(linhasObs, L, y); y += linhasObs.length * 4.5;
      }

      // ===== RODAPE =====
      const H = doc.internal.pageSize.getHeight();
      const FH = 27;
      doc.setFillColor(250, 248, 246);
      doc.rect(0, H - FH, 297, FH, "F");
      const fy = H - FH + 6;
      doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(26, 34, 43);
      doc.text(E.nome || "Empresa", L, fy);
      doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); doc.setTextColor(154, 161, 173);
      let ly = fy + 5;
      if (E.cnpj) { doc.text("CNPJ " + E.cnpj, L, ly); ly += 4.0; }
      const endL = [E.endereco, E.cidade].filter(Boolean).join(" - ");
      if (endL) { doc.text(endL, L, ly); ly += 4.0; }
      doc.text("Proposta válida até " + fmtDataBR(o.validade), L, ly);
      const cx = L + 115;
      doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); doc.setTextColor(238, 106, 26);
      doc.text("CONTATOS", cx, fy);
      doc.setFont("helvetica", "normal"); doc.setFontSize(7.5); doc.setTextColor(75, 85, 99);
      let cy = fy + 4.5;
      (E.contatos || "").split(/[\n;]/).map(t => t.trim()).filter(Boolean).forEach(t => { doc.text(t, cx, cy); cy += 4.0; });
      doc.setDrawColor(199, 204, 214);
      doc.line(R - 55, H - 9, R, H - 9);
      doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(107, 114, 128);
      doc.text(o.nomeCliente || "Cliente", R - 27.5, H - 5.5, { align: "center" });
      doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(154, 161, 173);
      doc.text("Assinatura do cliente", R - 27.5, H - 2, { align: "center" });
      doc.setFillColor(ORANGE[0], ORANGE[1], ORANGE[2]);
      doc.rect(0, H - 1.5, 297, 1.5, "F");

      doc.save((o.numero || "orcamento") + ".pdf");
    } catch (e) { console.error(e); alert("Erro ao gerar PDF"); }
  };

  if (empresa && empresa.statusOrcamento) {
    Object.keys(empresa.statusOrcamento).forEach(k => { if (ST[k] && empresa.statusOrcamento[k]) ST[k].t = empresa.statusOrcamento[k]; });
  }
  if (empresa && empresa.statusOrcamentoCor) {
    Object.keys(empresa.statusOrcamentoCor).forEach(k => { if (ST[k] && empresa.statusOrcamentoCor[k]) ST[k].c = empresa.statusOrcamentoCor[k]; });
  }

  return (
    <div style={{ padding:"24px 28px", background:"var(--page-bg)", minHeight:"100vh" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
        <h1 style={{ fontSize:26, fontWeight:800, color:"var(--text-primary)", margin:0 }}>Orçamentos</h1>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          <div style={{ display:"flex", border:"1px solid var(--border-color)", borderRadius:8, overflow:"hidden" }}>
            <button onClick={() => { setView("lista"); try{localStorage.setItem("orc_view","lista");}catch(e){} }} style={{ padding:"7px 16px", fontSize:13, fontWeight:600, border:"none", cursor:"pointer", background: view==="lista" ? "#EE6A1A" : "var(--card-bg)", color: view==="lista" ? "var(--card-bg)" : "var(--text-secondary)" }}>Lista</button>
            <button onClick={() => { setView("kanban"); try{localStorage.setItem("orc_view","kanban");}catch(e){} }} style={{ padding:"7px 16px", fontSize:13, fontWeight:600, border:"none", cursor:"pointer", background: view==="kanban" ? "#EE6A1A" : "var(--card-bg)", color: view==="kanban" ? "var(--card-bg)" : "var(--text-secondary)" }}>Kanban</button>
            {verValores && <button onClick={() => { setView("dashboard"); try{localStorage.setItem("orc_view","dashboard");}catch(e){} }} style={{ padding:"7px 16px", fontSize:13, fontWeight:600, border:"none", cursor:"pointer", background: view==="dashboard" ? "#EE6A1A" : "var(--card-bg)", color: view==="dashboard" ? "var(--card-bg)" : "var(--text-secondary)" }}>Dashboard</button>}
          </div>
          {verValores && <Btn onClick={() => setModal({ novo:true, itens:[], status:"rascunho", dataEmissao:new Date().toISOString().slice(0,10) })} cor="#EE6A1A" full>+ Novo orçamento</Btn>}
        </div>
      </div>

      <div style={{ display:"flex", gap:10, marginBottom:12, alignItems:"center", flexWrap:"wrap" }}>
        <button onClick={() => { const d=new Date(); setMesRef({ ano:d.getFullYear(), mes:d.getMonth() }); setVerTodos(false); }} style={{ padding:"6px 14px", fontSize:12.5, fontWeight:600, borderRadius:8, cursor:"pointer", border:"1px solid var(--border-color)", background:"var(--card-bg)", color:"var(--text-secondary)" }}>Mês atual</button>
        <div style={{ display:"flex", alignItems:"center", gap:6, opacity: verTodos?0.35:1, pointerEvents: verTodos?"none":"auto" }}>
          <button onClick={() => { const m=mesRef.mes-1; setMesRef(m<0 ? { ano:mesRef.ano-1, mes:11 } : { ano:mesRef.ano, mes:m }); }} style={{ background:"none", border:"none", cursor:"pointer", fontSize:22, color:"#EE6A1A", lineHeight:1, padding:"0 4px" }}>‹</button>
          <span style={{ fontSize:14, fontWeight:800, color:"var(--text-primary)", textTransform:"capitalize", minWidth:130, textAlign:"center" }}>{new Date(mesRef.ano, mesRef.mes, 1).toLocaleDateString("pt-BR", { month:"long", year:"numeric" })}</span>
          <button onClick={() => { const m=mesRef.mes+1; setMesRef(m>11 ? { ano:mesRef.ano+1, mes:0 } : { ano:mesRef.ano, mes:m }); }} style={{ background:"none", border:"none", cursor:"pointer", fontSize:22, color:"#EE6A1A", lineHeight:1, padding:"0 4px" }}>›</button>
        </div>
        <button onClick={() => setVerTodos(v => !v)} style={{ padding:"6px 14px", fontSize:12.5, fontWeight:600, borderRadius:8, cursor:"pointer", border:"1px solid var(--border-color)", background: verTodos ? "#EE6A1A" : "var(--card-bg)", color: verTodos ? "#fff" : "var(--text-secondary)" }}>Todos</button>
        <button onClick={() => setVerArquivados(v => !v)} style={{ padding:"6px 14px", fontSize:12.5, fontWeight:600, borderRadius:8, cursor:"pointer", border:"1px solid var(--border-color)", background: verArquivados ? "#6B7280" : "var(--card-bg)", color: verArquivados ? "#fff" : "var(--text-secondary)" }}>Arquivados</button>
        <input value={buscaOrc} onChange={e=>setBuscaOrc(e.target.value)} placeholder="Buscar cliente, nº, assunto, data, valor..." style={{ marginLeft:"auto", padding:"6px 12px", fontSize:12.5, borderRadius:8, border:"1px solid var(--border-color)", background:"var(--card-bg)", color:"var(--text-primary)", outline:"none", minWidth:230 }} />
        {verValores && <button onClick={gerarRelatorioPDF} style={{ padding:"6px 14px", fontSize:12.5, fontWeight:600, color:"#fff", background:"#EE6A1A", border:"none", borderRadius:8, cursor:"pointer" }}>Relatório PDF</button>}
        <span style={{ fontSize:12, color:"var(--text-muted)" }}>{visLista.length} orçamento{visLista.length!==1?"s":""}</span>
      </div>

      <div style={{ display:"flex", gap:12, marginBottom:16, flexWrap:"wrap" }}>
        <Card n={baseCards.length} label="Todos" cor="#98A2B3" Icon={FileText} ativo={filtro==="todos"} onClick={() => setFiltro("todos")} />
        {Object.keys(ST).map(k => (
          <Card key={k} n={verValores ? fmt(somaPor(k)) : baseCards.filter(o => o.status === k).length} label={ST[k].t} cor={ST[k].c} Icon={ICONE_ST[k]} count={baseCards.filter(o => o.status === k).length} ativo={filtro===k} onClick={() => setFiltro(k)} />
        ))}
      </div>

      {view === "kanban" ? (
        <KanbanOrcamentos
          verValores={verValores}
          lista={baseCards}
          onEditar={(o) => setModal({ ...o, dataEmissao:toInput(o.dataEmissao), validade:toInput(o.validade) })}
          onMover={mudarStatus}
          onPDF={gerarPDF}
          onSalvarCampo={salvarCampo}
          onRenomear={renomearStatus}
          onCor={renomearCor}
          onExcluir={excluir}
          onArquivar={arquivar}
          onClonar={clonar}
        />
      ) : (view === "dashboard" && verValores) ? (
        <DashboardOrcamentos lista={base} />
      ) : (
      <div style={{ background:"var(--card-bg)", borderRadius:10, border:"1px solid var(--border-color)", overflow:"auto" }}>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead>
            <tr>
              <th style={th}>Data</th>
              <th style={th}>Número</th>
              <th style={th}>{t("Cliente")}</th>
              <th style={th}>Assunto</th>
              <th style={th}>{t("Vendedor")}</th>
              <th style={th}>Status</th>
              <th style={th}>Prioridade</th>
              <th style={th}>Validade</th>
              {verValores && <th style={{ ...th, textAlign:"right" }}>Total</th>}
              <th style={{ ...th, textAlign:"center", width:270 }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {gruposMes.map(g => (
            <Fragment key={g.chave}>
              <tr>
                <td colSpan={verValores ? 10 : 9} style={{ ...td, background:"var(--hover-bg)", fontWeight:800, color:"var(--text-primary)", textTransform:"capitalize" }}>{g.data.toLocaleDateString("pt-BR", { month:"long", year:"numeric" })}<span style={{ fontWeight:600, color:"var(--text-muted)", marginLeft:8 }}> · {g.itens.length}{verValores ? " · " + fmt(g.itens.reduce((a,x)=>a+Number(x.total||0),0)) : ""}</span></td>
              </tr>
              {g.itens.map((o) => (
              <tr key={o._id}>
                <td style={td}>{fmtDataBR(o.dataEmissao)}</td>
                <td style={{ ...td, fontWeight:700, color:"#EE6A1A", whiteSpace:"nowrap" }}>{o.numero}{(o.anexos && o.anexos.length > 0) ? <span title={o.anexos.length + " anexo(s)"} style={{ display:"inline-flex", alignItems:"center", gap:2, marginLeft:6, fontSize:10, fontWeight:700, color:"#579bfc", verticalAlign:"middle" }}><Paperclip size={12} />{o.anexos.length}</span> : null}</td>
                <td style={td}>
                  <div style={{ fontWeight:600, color:"var(--text-primary)" }}>{o.nomeCliente || "—"}</div>
                  {o.nomeNegociacao && (
                    <div style={{ fontSize:11, color:"#98A2B3", marginTop:2 }}>↳ {o.nomeNegociacao}</div>
                  )}
                </td>
                <td style={td}>{o.titulo || "—"}</td>
                <td style={td}>
                  {o.nomeResponsavel
                    ? <span style={{ display:"inline-block", padding:"3px 10px", borderRadius:12, fontSize:11, fontWeight:600, color:"#EE6A1A", background:"#EE6A1A1A", border:"1px solid #EE6A1A40", whiteSpace:"nowrap" }}>{o.nomeResponsavel}</span>
                    : <span style={{ color:"#98A2B3" }}>—</span>}
                </td>
                <td style={td}><Badge s={o.status} /></td>
                <td style={td}>{(() => { const p = PRI[o.prioridade] || PRI.media; return <span style={{ display:"inline-block", padding:"3px 10px", borderRadius:12, fontSize:11, fontWeight:600, color:p.c, background:p.c+"1A", border:`1px solid ${p.c}40` }}>{p.t}</span>; })()}</td>
                <td style={td}>{fmtDataBR(o.validade)}</td>
                {verValores && <td style={{ ...td, textAlign:"right", fontWeight:700, color:"#1D9E75" }}>{fmt(o.total)}</td>}
                <td style={{ ...td, textAlign:"center" }}>
                  <div style={{ display:"flex", gap:6, justifyContent:"center" }}>
                    {verValores && <Btn onClick={() => setModal({ ...o, dataEmissao:toInput(o.dataEmissao), validade:toInput(o.validade) })} cor="#579bfc">Editar</Btn>}
                    {verValores && <Btn onClick={() => gerarPDF(o)} cor="#EE6A1A">PDF</Btn>}
                    {verValores && <Btn onClick={() => clonar(o)} cor="#8b5cf6">Clonar</Btn>}
                    {verValores && <Btn onClick={() => arquivar(o._id, !o.arquivado)} cor="#6B7280">{o.arquivado ? "Restaurar" : "Arquivar"}</Btn>}
                    {verValores && <Btn onClick={() => excluir(o._id)} cor="#E24B4A">Excluir</Btn>}
                  </div>
                </td>
              </tr>
              ))}
            </Fragment>
          ))}
            {!visLista.length && (
              <tr><td colSpan={verValores ? 10 : 9} style={{ ...td, textAlign:"center", color:"#98A2B3", padding:30 }}>{load ? "Carregando..." : "Nenhum orçamento."}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      )}

      {modal && (
        <ModalOrcamento
          dados={modal}
          clientes={clientes}
          negocs={negocs}
          catalogo={catalogo}
          usuarios={usuarios}
          currentUser={currentUser}
          onFechar={() => setModal(null)}
          onSalvo={() => { setModal(null); carregar(); }}
          onExcluir={excluir}
          onCatalogoMudou={recarregarCatalogo}
          onClienteCriado={(c) => setClientes(prev => [c, ...prev])}
        />
      )}

    </div>
  );
}

function TituloColuna({ label, cor, count, onSalvar, onCor }) {
  const [editando, setEditando] = useState(false);
  const [paleta, setPaleta] = useState(false);
  const [txt, setTxt] = useState(label);
  const ref = useRef(null);
  useEffect(() => { setTxt(label); }, [label]);
  useEffect(() => {
    const fora = (e) => { if (ref.current && !ref.current.contains(e.target)) setPaleta(false); };
    document.addEventListener("mousedown", fora);
    return () => document.removeEventListener("mousedown", fora);
  }, []);
  const salvar = () => { setEditando(false); const t = txt.trim(); if (t && t !== label) onSalvar(t); };
  const CORES = ["#98A2B3", "#579bfc", "#EBA13A", "#EE6A1A", "#E24B4A", "#1D9E75", "#a25ddc"];
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div ref={ref} style={{ position: "relative" }}>
        <button onClick={() => setPaleta(p => !p)} title="Mudar cor"
          style={{ width: 13, height: 13, borderRadius: "50%", background: cor, border: "2px solid var(--card-bg)", boxShadow: "0 0 0 1px " + cor, cursor: "pointer", padding: 0 }} />
        {paleta && (
          <div style={{ position: "absolute", top: "100%", left: 0, marginTop: 6, background: "var(--card-bg)", border: "1px solid var(--border-color)", borderRadius: 8, boxShadow: "0 8px 24px rgba(16,24,40,.15)", zIndex: 80, padding: 8, display: "flex", gap: 6 }}>
            {CORES.map(hex => (
              <button key={hex} onClick={() => { onCor(hex); setPaleta(false); }}
                style={{ width: 18, height: 18, borderRadius: "50%", background: hex, cursor: "pointer", border: cor === hex ? "2px solid var(--text-primary)" : "2px solid var(--card-bg)", boxShadow: "0 0 0 1px var(--border-color)" }} />
            ))}
          </div>
        )}
      </div>
      {editando ? (
        <input autoFocus value={txt} onChange={e => setTxt(e.target.value)} onBlur={salvar}
          onKeyDown={e => { if (e.key === "Enter") salvar(); if (e.key === "Escape") { setTxt(label); setEditando(false); } }}
          style={{ fontSize: 13, fontWeight: 800, color: "var(--text-primary)", border: "1px solid " + cor, borderRadius: 5, padding: "1px 6px", width: 128, outline: "none" }} />
      ) : (
        <span onClick={() => setEditando(true)} title="Clique para renomear" style={{ fontSize: 13, fontWeight: 800, color: "var(--text-primary)", cursor: "text" }}>{label}</span>
      )}
      <span style={{ fontSize: 11, color: "#98A2B3", background: "var(--card-bg)", borderRadius: 10, padding: "1px 8px" }}>{count}</span>
    </div>
  );
}
function SeletorPrioridade({ valor, onChange }) {
  const [aberto, setAberto] = useState(false);
  const ref = useRef(null);
  const atual = PRI[valor] || PRI.media;
  useEffect(() => {
    const fora = (e) => { if (ref.current && !ref.current.contains(e.target)) setAberto(false); };
    document.addEventListener("mousedown", fora);
    return () => document.removeEventListener("mousedown", fora);
  }, []);
  return (
    <div ref={ref} style={{ position: "relative" }} onClick={e => e.stopPropagation()}>
      <button onClick={() => setAberto(a => !a)} title={"Prioridade: " + atual.t}
        style={{ display: "flex", alignItems: "center", gap: 4, background: atual.c + "18", border: "1px solid " + atual.c + "55", borderRadius: 6, padding: "2px 7px", cursor: "pointer", color: atual.c, fontSize: 10, fontWeight: 700 }}>
        <svg width="10" height="12" viewBox="0 0 10 12" fill="none"><path d="M1.2 0.5v11" stroke={atual.c} strokeWidth="1.3" strokeLinecap="round"/><path d="M2 1.2h6l-1.5 2 1.5 2H2z" fill={atual.c}/></svg>
        {atual.t}
      </button>
      {aberto && (
        <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 3, background: "var(--card-bg)", border: "1px solid var(--border-color)", borderRadius: 8, boxShadow: "0 8px 24px rgba(16,24,40,.15)", zIndex: 70, overflow: "hidden", minWidth: 112 }}>
          {Object.keys(PRI).map(k => (
            <div key={k} onClick={() => { onChange(k); setAberto(false); }}
              style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 11px", cursor: "pointer", fontSize: 12, fontWeight: 600, color: PRI[k].c, background: valor === k ? PRI[k].c + "12" : "var(--card-bg)" }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: PRI[k].c }} />
              {PRI[k].t}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DashboardOrcamentos({ lista }) {
  const hoje = new Date();
  const [modo, setModo] = useState("mes");
  const [ano, setAno] = useState(hoje.getFullYear());
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  const [de, setDe] = useState(hoje.getFullYear() + "-01-01");
  const [ate, setAte] = useState(new Date().toISOString().slice(0, 10));

  const per = modo === "mes"
    ? { de: ano + "-" + String(mes).padStart(2, "0") + "-01", ate: ano + "-" + String(mes).padStart(2, "0") + "-" + new Date(ano, mes, 0).getDate() }
    : modo === "ano" ? { de: ano + "-01-01", ate: ano + "-12-31" } : { de, ate };

  const dentro = (o) => { const d = String(o.dataEmissao || "").slice(0, 10); return d && d >= per.de && d <= per.ate; };
  const dados = lista.filter(dentro);
  const somaStatus = (k) => dados.filter(o => o.status === k).reduce((a, o) => a + Number(o.total || 0), 0);
  const countStatus = (k) => dados.filter(o => o.status === k).length;
  const totalGeral = dados.reduce((a, o) => a + Number(o.total || 0), 0);
  const ganhosKeys = ["aprovado", "recusado", "expirado"];
  const emGanho = (o) => ganhosKeys.includes(o.status);
  const valGanho = dados.filter(emGanho).reduce((a, o) => a + Number(o.total || 0), 0);
  const valAberto = totalGeral - valGanho;
  const nGanho = dados.filter(emGanho).length;
  const conv = dados.length ? Math.round((nGanho / dados.length) * 100) : 0;

  const vend = {};
  dados.forEach(o => {
    const v = o.nomeResponsavel || "Sem vendedor";
    if (!vend[v]) vend[v] = { nome: v, total: 0, ganho: 0, count: 0 };
    vend[v].total += Number(o.total || 0);
    vend[v].count += 1;
    if (emGanho(o)) vend[v].ganho += Number(o.total || 0);
  });
  const ranking = Object.values(vend).sort((a, b) => b.total - a.total);

  const selInp = { padding: "7px 10px", border: "1px solid var(--border-color)", borderRadius: 6, fontSize: 13, background: "var(--card-bg)", color: "var(--text-primary)" };
  const btnP = (on) => ({ padding: "7px 14px", fontSize: 13, fontWeight: 600, borderRadius: 8, cursor: "pointer", border: "1px solid var(--border-color)", background: on ? "#EE6A1A" : "var(--card-bg)", color: on ? "var(--card-bg)" : "var(--text-secondary)" });
  const cardMini = { flex: 1, minWidth: 180, padding: "16px 18px", borderRadius: 10, background: "var(--card-bg)", border: "1px solid var(--border-color)" };
  const lblMini = { fontSize: 12, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: .4 };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
        <button onClick={() => setModo("mes")} style={btnP(modo === "mes")}>Mensal</button>
        <button onClick={() => setModo("ano")} style={btnP(modo === "ano")}>Anual</button>
        <button onClick={() => setModo("intervalo")} style={btnP(modo === "intervalo")}>Intervalo</button>
        {modo === "mes" && (<>
          <select style={selInp} value={mes} onChange={e => setMes(+e.target.value)}>{["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"].map((m, i) => <option key={i} value={i + 1}>{m}</option>)}</select>
          <select style={selInp} value={ano} onChange={e => setAno(+e.target.value)}>{[ano - 2, ano - 1, ano, ano + 1].map(a => <option key={a} value={a}>{a}</option>)}</select>
        </>)}
        {modo === "ano" && (<select style={selInp} value={ano} onChange={e => setAno(+e.target.value)}>{[ano - 2, ano - 1, ano, ano + 1].map(a => <option key={a} value={a}>{a}</option>)}</select>)}
        {modo === "intervalo" && (<>
          <input type="date" style={selInp} value={de} onChange={e => setDe(e.target.value)} />
          <input type="date" style={selInp} value={ate} onChange={e => setAte(e.target.value)} />
        </>)}
        <span style={{ marginLeft: "auto", fontSize: 13, color: "var(--text-muted)" }}>{dados.length} orcamentos no periodo</span>
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        {Object.keys(ST).map(k => { const o = ST[k]; return (
          <div key={k} style={{ flex: 1, minWidth: 150, padding: "14px 16px", borderRadius: 10, background: o.c + "14", border: "1px solid " + o.c + "33" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: o.c, textTransform: "uppercase", letterSpacing: .4 }}>{o.t}</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "var(--text-primary)", marginTop: 4 }}>{fmt(somaStatus(k))}</div>
            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{countStatus(k)} {countStatus(k) === 1 ? "orcamento" : "orcamentos"}</div>
          </div>
        ); })}
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={cardMini}>
          <div style={lblMini}>Taxa de conversao</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: "#1D9E75" }}>{conv}%</div>
          <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{nGanho} de {dados.length} orcamentos</div>
        </div>
        <div style={cardMini}>
          <div style={lblMini}>Valor convertido</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#1D9E75" }}>{fmt(valGanho)}</div>
        </div>
        <div style={cardMini}>
          <div style={lblMini}>Em aberto</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#EBA13A" }}>{fmt(valAberto)}</div>
        </div>
        <div style={cardMini}>
          <div style={lblMini}>Total no periodo</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "var(--text-primary)" }}>{fmt(totalGeral)}</div>
        </div>
      </div>

      <div style={{ background: "var(--card-bg)", borderRadius: 10, border: "1px solid var(--border-color)", overflow: "hidden" }}>
        <div style={{ padding: "12px 16px", fontSize: 14, fontWeight: 700, color: "var(--text-primary)", borderBottom: "1px solid var(--border-color)" }}>{t("Ranking por vendedor")}</div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>
            <th style={th}>{t("Vendedor")}</th>
            <th style={{ ...th, textAlign: "center" }}>Orcamentos</th>
            <th style={{ ...th, textAlign: "right" }}>Total orcado</th>
            <th style={{ ...th, textAlign: "right" }}>Convertido</th>
            <th style={{ ...th, textAlign: "center" }}>Conversao</th>
          </tr></thead>
          <tbody>
            {ranking.map(v => { const pct = v.total ? Math.round((v.ganho / v.total) * 100) : 0; return (
              <tr key={v.nome}>
                <td style={{ ...td, fontWeight: 600, color: "var(--text-primary)" }}>{v.nome}</td>
                <td style={{ ...td, textAlign: "center" }}>{v.count}</td>
                <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{fmt(v.total)}</td>
                <td style={{ ...td, textAlign: "right", color: "#1D9E75", fontWeight: 700 }}>{fmt(v.ganho)}</td>
                <td style={{ ...td, textAlign: "center" }}>{pct}%</td>
              </tr>
            ); })}
            {!ranking.length && <tr><td colSpan={5} style={{ ...td, textAlign: "center", color: "var(--text-muted)", padding: 24 }}>{t("Nenhum orcamento no periodo.")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KanbanOrcamentos({ verValores, lista, onEditar, onMover, onPDF, onSalvarCampo, onRenomear, onCor, onExcluir, onArquivar, onClonar }) {
  const [arrastando, setArrastando] = useState(null);
  const [sobre, setSobre] = useState(null);
  const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
  const colunas = Object.keys(ST);

  const soltar = (status) => {
    if (arrastando && arrastando.status !== status) onMover(arrastando._id, status);
    setArrastando(null); setSobre(null);
  };

  return (
    <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 8, alignItems: "flex-start" }}>
      {colunas.map(status => {
        const o = ST[status];
        const cards = lista.filter(x => x.status === status);
        const total = cards.reduce((a, c) => a + Number(c.total || 0), 0);
        return (
          <div key={status}
            onDragOver={e => { e.preventDefault(); setSobre(status); }}
            onDragLeave={() => setSobre(s => s === status ? null : s)}
            onDrop={(e) => { e.preventDefault(); let _id = ""; try { _id = e.dataTransfer.getData("text/plain"); } catch(_err){} if (!_id && arrastando) _id = arrastando._id; if (_id) onMover(_id, status); setArrastando(null); setSobre(null); }}
            style={{ flex: "0 0 300px", minWidth: 0, overflow: "hidden", background: sobre === status ? o.c + "14" : "var(--border-light)", borderRadius: 12, border: "1px solid " + (sobre === status ? o.c : "var(--border-color)"), minHeight: 220, transition: "background .12s" }}>
            <div style={{ padding: "12px 14px", borderBottom: "2px solid " + o.c, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <TituloColuna label={o.t} cor={o.c} count={cards.length} onSalvar={(t) => onRenomear(status, t)} onCor={(cor) => onCor(status, cor)} />
              <span style={{ fontSize: 12, fontWeight: 700, color: "#1D9E75" }}>{verValores ? fmt(total) : ""}</span>
            </div>
            <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 10 }}>
              {cards.map(c => {
                const venc = c.validade ? new Date(c.validade) : null;
                const vencida = venc && venc < hoje && !["aprovado", "recusado"].includes(c.status);
                const pri = PRI[c.prioridade] || PRI.media;
                return (
                  <div key={c._id}
                    draggable
                    onDragStart={(e) => { setArrastando(c); try { e.dataTransfer.setData("text/plain", c._id || ""); e.dataTransfer.effectAllowed = "move"; } catch(_err){} }}
                    onDragEnd={() => { setArrastando(null); setSobre(null); }}
                    style={{ background: "var(--card-bg)", borderRadius: 10, border: "1px solid var(--border-color)", padding: 12, cursor: "grab", userSelect: "none", WebkitUserSelect: "none", boxShadow: "0 1px 2px rgba(16,24,40,.05)", minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <span style={{ display:"flex", alignItems:"center", gap:6 }}><span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", fontFamily: "monospace" }}>{c.numero}</span>{(c.anexos && c.anexos.length > 0) ? <span title={c.anexos.length + " anexo(s)"} style={{ display:"inline-flex", alignItems:"center", gap:2, fontSize:10, fontWeight:700, color:"#579bfc" }}><Paperclip size={12} />{c.anexos.length}</span> : null}</span>
                      <SeletorPrioridade valor={c.prioridade} onChange={(k) => onSalvarCampo(c._id, { prioridade: k })} />
                    </div>
                    <div onClick={verValores ? () => onEditar(c) : undefined} style={{ cursor: verValores ? "pointer" : "default" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 2 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>{c.nomeCliente || "-"}</span>
                        <span style={{ fontSize: 13, fontWeight: 800, color: "#1D9E75", whiteSpace: "nowrap" }}>{verValores ? fmt(c.total) : ""}</span>
                      </div>
                      {c.titulo && <div style={{ fontSize: 11, color: "var(--text-secondary)", marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.titulo}</div>}
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6, marginTop: 4 }} onClick={e => e.stopPropagation()}>
                      {c.nomeResponsavel
                        ? <span style={{ fontSize: 10, fontWeight: 600, color: "var(--text-secondary)", background: "var(--border-light)", borderRadius: 8, padding: "2px 7px" }}>{c.nomeResponsavel}</span>
                        : <span />}
                      <input type="date" value={toInput(c.validade)}
                        onChange={e => onSalvarCampo(c._id, { validade: e.target.value || null })}
                        style={{ fontSize: 10, border: "1px solid var(--border-color)", borderRadius: 6, padding: "2px 5px", color: vencida ? "#E24B4A" : "var(--text-secondary)", fontWeight: vencida ? 700 : 400, outline: "none" }} />
                    </div>
                    <div style={{ fontSize: 10, color: "var(--text-secondary)", fontWeight: 600, marginTop: 4 }}>Emissão: {fmtDataBR(c.dataEmissao)}</div>{c.nomeResponsavelFinanceiro && <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 2 }}>💰 Financeiro: {c.nomeResponsavelFinanceiro}</div>}
                    {(() => { const won = ["aprovado","recusado","expirado"].includes(c.status); const hojeS = new Date().toISOString().slice(0,10); const ref = won ? (c.dataAprovacao || c.dataEmissao || c.createdAt) : (c.dataEmissao || c.createdAt); if (true /* botao aprovar removido */ || won || !ref || String(ref).slice(0,7) >= hojeS.slice(0,7)) return null; return (
                      <button onClick={(e) => { e.stopPropagation(); onSalvarCampo(c._id, won ? { dataAprovacao: hojeS } : { status:"aprovado", dataAprovacao: hojeS }); }} title="Definir aprovação no mês atual" style={{ width:"100%", marginTop:8, fontSize:11, fontWeight:700, color:"#fff", background:"#1D9E75", border:"none", borderRadius:6, padding:"6px 0", cursor:"pointer" }}>{won ? "↻ Mover p/ mês atual" : "✓ Aprovar (mês atual)"}</button>
                    ); })()}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8, borderTop: "1px solid var(--border-light)", paddingTop: 8, alignItems: "center" }} onClick={e => e.stopPropagation()}>
                      <select value={c.status} onChange={e => onMover(c._id, e.target.value)}
                        style={{ flex: 1, fontSize: 10, border: "1px solid var(--border-color)", borderRadius: 6, padding: "4px 4px", cursor: "pointer", color: "var(--text-secondary)", outline: "none" }}>
                        {colunas.map(k => <option key={k} value={k}>{ST[k].t}</option>)}
                      </select>
                      {verValores && <button onClick={() => onPDF(c)} style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", background: "none", border: "1px solid var(--border-color)", borderRadius: 6, padding: "4px 8px", cursor: "pointer" }}>PDF</button>}
                      {verValores && <button onClick={() => onClonar(c)} style={{ fontSize: 11, fontWeight: 600, color: "#8b5cf6", background: "none", border: "1px solid var(--border-color)", borderRadius: 6, padding: "4px 8px", cursor: "pointer" }}>Clonar</button>}
                      {verValores && <button onClick={() => onEditar(c)} style={{ fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", background: "none", border: "1px solid var(--border-color)", borderRadius: 6, padding: "4px 8px", cursor: "pointer" }}>Editar</button>}
                      {verValores && <button onClick={() => onArquivar(c._id, !c.arquivado)} title={c.arquivado ? "Restaurar" : "Arquivar"} style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", background: "none", border: "1px solid var(--border-color)", borderRadius: 6, padding: "4px 8px", cursor: "pointer" }}>{c.arquivado ? "Restaurar" : "Arquivar"}</button>}
                      {verValores && <button onClick={() => onExcluir(c._id)} title="Excluir" style={{ fontSize: 11, fontWeight: 600, color: "#E24B4A", background: "none", border: "1px solid var(--border-color)", borderRadius: 6, padding: "4px 8px", cursor: "pointer" }}>Excluir</button>}
                    </div>
                  </div>
                );
              })}
              {!cards.length && (
                <div style={{ fontSize: 11, color: "#98A2B3", textAlign: "center", padding: "16px 0" }}>Arraste aqui</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ModalClienteRapido({ onFechar, onCriado }) {
  const [f, setF] = useState({ nomeEmpresa: "", nomeContato: "", cnpj: "", telefone: "", email: "" });
  const [salvando, setSalvando] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const salvar = async () => {
    if (!f.nomeEmpresa.trim()) return alert("Informe o nome da empresa.");
    setSalvando(true);
    try {
      const { data } = await api.post("/clientes", { ...f, status: "prospect" });
      onCriado(data);
    } catch (e) {
      alert("Erro ao cadastrar: " + (e?.response?.data?.erro || e.message));
    }
    setSalvando(false);
  };
  return (
    <div onClick={onFechar} style={{ position: "fixed", inset: 0, background: "rgba(16,24,40,.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1100, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "var(--card-bg)", borderRadius: 12, width: "100%", maxWidth: 560, padding: 22, boxShadow: "0 20px 60px rgba(0,0,0,.3)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: "#1D9E75" }}>{t("Cadastrar cliente")}</h3>
          <button onClick={onFechar} style={{ border: "none", background: "none", fontSize: 22, color: "var(--text-muted)", cursor: "pointer", lineHeight: 1 }}>x</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
          <div style={{ gridColumn: "span 2" }}>
            <label style={lbl}>{t("Empresa / Cliente *")}</label>
            <input autoFocus style={inp} value={f.nomeEmpresa} onChange={e => set("nomeEmpresa", e.target.value)} placeholder={t("Ex: LDC Rondonopolis")} />
          </div>
          <div>
            <label style={lbl}>{t("Contato")}</label>
            <input style={inp} value={f.nomeContato} onChange={e => set("nomeContato", e.target.value)} placeholder={t("Nome do contato")} />
          </div>
          <div>
            <label style={lbl}>{t("CNPJ")}</label>
            <input style={inp} value={f.cnpj} onChange={e => set("cnpj", e.target.value)} placeholder={t("00.000.000/0001-00")} />
          </div>
          <div>
            <label style={lbl}>Telefone</label>
            <input style={inp} value={f.telefone} onChange={e => set("telefone", e.target.value)} />
          </div>
          <div>
            <label style={lbl}>E-mail</label>
            <input style={inp} value={f.email} onChange={e => set("email", e.target.value)} />
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 11, color: "var(--text-muted)" }}>Sera cadastrado e ja selecionado no orcamento.</span>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn onClick={onFechar} cor="#98A2B3">Cancelar</Btn>
            <Btn onClick={salvar} cor="#1D9E75" full>{salvando ? "Salvando..." : "Cadastrar"}</Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

function ModalCadastroRapido({ tipo, cor, onFechar, onCriado }) {
  const ehProduto = tipo === "produto";
  const [f, setF] = useState({ codigo:"", nome:"", categoria:"", marca:"", ncm:"", unidade:"un", custo:0, margem:0, preco:0 });
  const [salvando, setSalvando] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const aplicarMargem = () => {
    const c = Number(f.custo) || 0, m = Number(f.margem) || 0;
    if (!c) return;
    set("preco", Number((c * (1 + m / 100)).toFixed(2)));
  };

  const salvar = async () => {
    if (!f.nome) return alert("Informe o nome.");
    setSalvando(true);
    try {
      const { data } = await api.post("/catalogo", {
        ...f, tipo,
        custo: Number(f.custo) || 0,
        margem: Number(f.margem) || 0,
        preco: Number(f.preco) || 0,
      });
      onCriado(data);
    } catch (e) {
      alert("Erro ao cadastrar: " + (e?.response?.data?.erro || e.message));
    }
    setSalvando(false);
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(16,24,40,.6)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1100, padding:16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background:"var(--card-bg)", borderRadius:12, width:"100%", maxWidth:640, padding:22, boxShadow:"0 20px 60px rgba(0,0,0,.3)" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
          <h3 style={{ margin:0, fontSize:16, fontWeight:800, color:cor }}>
            Cadastrar {ehProduto ? "produto" : "serviço"} no catálogo
          </h3>
          <button onClick={onFechar} style={{ border:"none", background:"none", fontSize:22, color:"#98A2B3", cursor:"pointer", lineHeight:1 }}>×</button>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:10, marginBottom:12 }}>
          <div>
            <label style={lbl}>Código</label>
            <input style={inp} value={f.codigo} onChange={e => set("codigo", e.target.value)} placeholder="SKU" />
          </div>
          <div style={{ gridColumn:"span 3" }}>
            <label style={lbl}>Nome *</label>
            <input autoFocus style={inp} value={f.nome} onChange={e => set("nome", e.target.value)} placeholder={ehProduto ? "Ex: Câmera IP Bullet 4MP" : "Ex: Instalação de CFTV"} />
          </div>
          <div>
            <label style={lbl}>Categoria</label>
            <input style={inp} value={f.categoria} onChange={e => set("categoria", e.target.value)} />
          </div>
          {ehProduto && (
            <>
              <div>
                <label style={lbl}>Marca</label>
                <input style={inp} value={f.marca} onChange={e => set("marca", e.target.value)} />
              </div>
              <div>
                <label style={lbl}>NCM</label>
                <input
                  style={{ ...inp, fontFamily:"monospace" }}
                  value={f.ncm}
                  maxLength={10}
                  placeholder="0000.00.00"
                  onChange={e => {
                    const d = e.target.value.replace(/\D/g, "").slice(0, 8);
                    const v = d.length > 6 ? `${d.slice(0,4)}.${d.slice(4,6)}.${d.slice(6)}`
                      : d.length > 4 ? `${d.slice(0,4)}.${d.slice(4)}` : d;
                    set("ncm", v);
                  }}
                />
              </div>
            </>
          )}
          <div>
            <label style={lbl}>Unidade</label>
            <input style={inp} value={f.unidade} onChange={e => set("unidade", e.target.value)} />
          </div>
        </div>

        <div style={{ background:"var(--page-bg)", border:"1px solid var(--border-color)", borderRadius:8, padding:12, marginBottom:16 }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr auto 1fr", gap:10, alignItems:"end" }}>
            <div>
              <label style={lbl}>Custo (R$)</label>
              <input type="number" min="0" step="any" style={inp} value={f.custo} onChange={e => set("custo", e.target.value)} />
            </div>
            <div>
              <label style={lbl}>Margem (%)</label>
              <input type="number" min="0" step="any" style={inp} value={f.margem} onChange={e => set("margem", e.target.value)} />
            </div>
            <Btn onClick={aplicarMargem} cor="#579bfc">Calcular →</Btn>
            <div>
              <label style={lbl}>Preço venda *</label>
              <input type="number" min="0" step="any" style={{ ...inp, fontWeight:700, color:"#1D9E75" }} value={f.preco} onChange={e => set("preco", e.target.value)} />
            </div>
          </div>
        </div>

        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <span style={{ fontSize:11, color:"#98A2B3" }}>Será salvo no catálogo e adicionado a este orçamento.</span>
          <div style={{ display:"flex", gap:8 }}>
            <Btn onClick={onFechar} cor="#98A2B3">Cancelar</Btn>
            <Btn onClick={salvar} cor="#1D9E75" full>{salvando ? "Salvando..." : "Cadastrar e adicionar"}</Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

function LinhaBusca({ tipo, cor, catalogo, ehProduto, onEscolher, onAvulso, onCadastrar }) {
  const [q, setQ] = useState("");
  const [aberto, setAberto] = useState(false);
  const [hi, setHi] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const fora = (e) => { if (ref.current && !ref.current.contains(e.target)) setAberto(false); };
    document.addEventListener("mousedown", fora);
    return () => document.removeEventListener("mousedown", fora);
  }, []);

  const doCat = catalogo.filter(c => c.tipo === tipo);
  const termo = q.trim().toLowerCase();
  const filtrados = termo
    ? doCat.filter(c => `${c.nome} ${c.codigo || ""} ${c.marca || ""} ${c.categoria || ""}`.toLowerCase().includes(termo))
    : doCat;

  const escolher = (c) => { onEscolher(c._id); setQ(""); setAberto(false); setHi(0); };

  const onKey = (e) => {
    if (!aberto && (e.key === "ArrowDown" || e.key === "Enter")) { setAberto(true); return; }
    if (!aberto) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHi(h => Math.min(h + 1, filtrados.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi(h => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (filtrados[hi]) escolher(filtrados[hi]); }
    else if (e.key === "Escape") { setAberto(false); }
  };

  return (
    <tr>
      <td colSpan={ehProduto ? 9 : 8} ref={ref} style={{ padding: 8, position: "relative", background: cor + "08", borderTop: `1px solid ${cor}22` }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            value={q}
            onChange={e => { setQ(e.target.value); setAberto(true); setHi(0); }}
            onFocus={() => setAberto(true)}
            onKeyDown={onKey}
            placeholder={`+ Adicionar ${ehProduto ? "produto" : "serviço"} — busque por nome, código ou marca...`}
            style={{ ...inp, border: `1px solid ${cor}66`, background: "var(--card-bg)" }}
          />
          <Btn onClick={onAvulso} cor={cor}>+ Avulso</Btn>
          <Btn onClick={onCadastrar} cor="#1D9E75" full>+ Novo</Btn>
        </div>

        {aberto && (
          <div style={{ position: "absolute", left: 8, right: 8, top: "100%", zIndex: 60, background: "var(--card-bg)", border: "1px solid var(--border-color)", borderRadius: 8, boxShadow: "0 10px 30px rgba(16,24,40,.18)", maxHeight: 300, overflowY: "auto", marginTop: 2 }}>
            {filtrados.length === 0 && (
              <div style={{ padding: 14, fontSize: 12, color: "#98A2B3", textAlign: "center" }}>
                Nada encontrado. Use "+ Avulso" para item livre ou "+ Novo" para cadastrar.
              </div>
            )}
            {filtrados.map((c, idx) => (
              <div key={c._id}
                onMouseEnter={() => setHi(idx)}
                onClick={() => escolher(c)}
                style={{ padding: "9px 12px", cursor: "pointer", borderBottom: "1px solid var(--border-light)", background: idx === hi ? cor + "14" : "var(--card-bg)", display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.nome}</div>
                  <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 1 }}>
                    {c.codigo ? `Cód ${c.codigo}` : "sem código"}
                    {c.marca ? ` · ${c.marca}` : ""}
                    {c.categoria ? ` · ${c.categoria}` : ""}
                  </div>
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#1D9E75", whiteSpace: "nowrap" }}>{fmt(c.preco)}</div>
              </div>
            ))}
          </div>
        )}
      </td>
    </tr>
  );
}

function SecaoItens({ titulo, tipo, cor, itens, catalogo, onAddCatalogo, onAddAvulso, onCadastrar, setItem, delItem }) {
  const meus = itens.map((it, i) => ({ it, i })).filter(x => x.it.tipo === tipo);
  const subtotal = meus.reduce((s, { it }) =>
    s + ((Number(it.quantidade) || 0) * (Number(it.valorUnitario) || 0) - (Number(it.desconto) || 0)), 0);
  const ehProduto = tipo === "produto";

  return (
    <div style={{ marginBottom: 16, border: `1px solid ${cor}33`, borderRadius: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", background: cor + "0F", borderBottom: `1px solid ${cor}33` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 800, color: cor, textTransform: "uppercase", letterSpacing: .5 }}>{titulo}</span>
          <span style={{ fontSize: 11, color: "var(--text-secondary)" }}>{meus.length} {meus.length === 1 ? "item" : "itens"}</span>
          {subtotal > 0 && (
            <span style={{ fontSize: 12, fontWeight: 700, color: "#1D9E75" }}>· {fmt(subtotal)}</span>
          )}
        </div>
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", background: "var(--card-bg)" }}>
        <thead>
          <tr style={{ background: "var(--page-bg)" }}>
            <th style={{ ...th, width: 34, textAlign: "center" }}>#</th>
            <th style={th}>Descrição</th>
            {ehProduto && <th style={{ ...th, width: 110 }}>NCM</th>}
            <th style={{ ...th, width: 70 }}>Un.</th>
            <th style={{ ...th, width: 80 }}>Qtd</th>
            <th style={{ ...th, width: 120 }}>Unitário</th>
            <th style={{ ...th, width: 110 }}>Desc. R$</th>
            <th style={{ ...th, width: 120, textAlign: "right" }}>Total</th>
            <th style={{ ...th, width: 36 }}></th>
          </tr>
        </thead>
        <tbody>
          {meus.map(({ it, i }, n) => (
            <tr key={i}>
              <td style={{ ...td, textAlign: "center", color: "#98A2B3" }}>{n + 1}</td>
              <td style={td}><input style={inp} value={it.descricao} onChange={e => setItem(i, "descricao", e.target.value)} placeholder={ehProduto ? "Descrição do produto" : "Descrição do serviço"} /></td>
              {ehProduto && (
                <td style={td}>
                  <input
                    style={{ ...inp, fontFamily: "monospace" }}
                    value={it.ncm || ""}
                    maxLength={10}
                    placeholder="0000.00.00"
                    onChange={e => {
                      const d = e.target.value.replace(/\D/g, "").slice(0, 8);
                      const v = d.length > 6 ? `${d.slice(0, 4)}.${d.slice(4, 6)}.${d.slice(6)}`
                        : d.length > 4 ? `${d.slice(0, 4)}.${d.slice(4)}`
                          : d;
                      setItem(i, "ncm", v);
                    }}
                  />
                </td>
              )}
              <td style={td}><input style={inp} value={it.unidade} onChange={e => setItem(i, "unidade", e.target.value)} /></td>
              <td style={td}><input type="number" min="0" step="any" style={inp} value={it.quantidade} onChange={e => setItem(i, "quantidade", e.target.value)} /></td>
              <td style={td}><input type="number" min="0" step="any" style={inp} value={it.valorUnitario} onChange={e => setItem(i, "valorUnitario", e.target.value)} /></td>
              <td style={td}><input type="number" min="0" step="any" style={inp} value={it.desconto} onChange={e => setItem(i, "desconto", e.target.value)} /></td>
              <td style={{ ...td, textAlign: "right", fontWeight: 700, color: "var(--text-secondary)" }}>
                {fmt((Number(it.quantidade) || 0) * (Number(it.valorUnitario) || 0) - (Number(it.desconto) || 0))}
              </td>
              <td style={{ ...td, textAlign: "center" }}>
                <button onClick={() => delItem(i)} style={{ border: "none", background: "none", color: "#E24B4A", cursor: "pointer", fontSize: 16 }}>×</button>
              </td>
            </tr>
          ))}
          <LinhaBusca
            tipo={tipo}
            cor={cor}
            catalogo={catalogo}
            ehProduto={ehProduto}
            onEscolher={onAddCatalogo}
            onAvulso={onAddAvulso}
            onCadastrar={onCadastrar}
          />
        </tbody>
      </table>
    </div>
  );
}

function ModalOrcamento({ dados, clientes, negocs, catalogo, usuarios, currentUser, onFechar, onSalvo, onCatalogoMudou, onClienteCriado, onExcluir }) {
  const [f, setF] = useState({
    numero: dados.numero || "",
    titulo: dados.titulo || "",
    cliente: dados.cliente || "",
    nomeCliente: dados.nomeCliente || "",
    negociacao: dados.negociacao || "",
    nomeNegociacao: dados.nomeNegociacao || "",
    responsavel: dados.responsavel || currentUser?._id || "",
    nomeResponsavel: dados.nomeResponsavel || currentUser?.nome || "",
    responsavelFinanceiro: dados.responsavelFinanceiro || "",
    nomeResponsavelFinanceiro: dados.nomeResponsavelFinanceiro || "",
    status: dados.status || "rascunho",
    prioridade: dados.prioridade || "media",
    dataEmissao: dados.dataEmissao || new Date().toISOString().slice(0,10),
    validade: dados.validade || "",
    dataAprovacao: toInput(dados.dataAprovacao) || "",
    descontoGeral: dados.descontoGeral || 0,
    condicoesPagamento: dados.condicoesPagamento || "",
    prazoEntrega: dados.prazoEntrega || "",
    observacoes: dados.observacoes || "",
  });
  const [itens, setItens] = useState(dados.itens && dados.itens.length ? dados.itens : []);
  const [salvando, setSalvando] = useState(false);
  const [todasNegocs, setTodasNegocs] = useState(false);
  const [cadastroRapido, setCadastroRapido] = useState(null);
  const [clienteRapido, setClienteRapido] = useState(false);

  // Auto-preenche o NCM dos itens vazios a partir do catalogo (ao abrir/editar)
  useEffect(() => {
    if (!catalogo || !catalogo.length) return;
    const byName = {};
    catalogo.forEach(c => { if (c.ncm) { const k = String(c.nome||"").trim().toLowerCase(); if (k) byName[k] = c.ncm; } });
    setItens(prev => {
      let mudou = false;
      const novo = prev.map(it => {
        if (!it.ncm) { const ncm = byName[String(it.descricao||"").trim().toLowerCase()]; if (ncm) { mudou = true; return { ...it, ncm }; } }
        return it;
      });
      return mudou ? novo : prev;
    });
  }, [catalogo]);

  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  // Rótulo de uma negociação: prioriza o SERVIÇO
  const rotuloNegoc = (n) => {
    const serv = (n.servico || "").trim();
    const quem = (n.nomeCliente || n.empresa || n.nome || "").trim();
    if (serv && quem) return `${serv} — ${quem}`;
    return serv || quem || "—";
  };

  // Negociações do cliente selecionado (casa por nomeCliente / empresa / nome)
  const norm = (t) => String(t || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const negocsDoCliente = (() => {
    if (!f.cliente || todasNegocs) return negocs;
    const c = clientes.find(x => x._id === f.cliente);
    if (!c) return negocs;
    const alvo = norm(c.nomeEmpresa || c.nomeContato);
    if (!alvo) return negocs;
    const bate = (v) => {
      const x = norm(v);
      return x && (x.includes(alvo) || alvo.includes(x));
    };
    const filtradas = negocs.filter(n => bate(n.nomeCliente) || bate(n.empresa) || bate(n.nome));
    return filtradas.length ? filtradas : negocs;
  })();

  const addItem = (base) => setItens(p => [...p, base || { tipo:"produto", descricao:"", ncm:"", unidade:"un", quantidade:1, valorUnitario:0, desconto:0 }]);
  const setItem = (i, k, v) => setItens(p => p.map((it, n) => n === i ? { ...it, [k]: v } : it));
  const delItem = (i) => setItens(p => p.filter((_, n) => n !== i));

  const addDoCatalogo = (id) => {
    const c = catalogo.find(x => x._id === id);
    if (!c) return;
    const preco = c.tipo === "servico" ? 0 : (c.preco || 0);
    addItem({ tipo:c.tipo, descricao:c.nome, ncm:c.ncm || "", unidade:c.unidade || "un", quantidade:1, valorUnitario:preco, desconto:0 });
  };

  const somaDe = (arr) => arr.reduce((s, i) => s + ((Number(i.quantidade)||0) * (Number(i.valorUnitario)||0) - (Number(i.desconto)||0)), 0);
  const totProdutos = somaDe(itens.filter(i => i.tipo === "produto"));
  const totServicos = somaDe(itens.filter(i => i.tipo === "servico"));
  const subtotal = totProdutos + totServicos;
  const total = Math.max(0, subtotal - (Number(f.descontoGeral) || 0));

  const salvar = async () => {
    if (!f.cliente) return alert("Selecione o cliente.");
    if (!f.responsavel) return alert("Selecione o vendedor.");
    if (!itens.length) return alert("Adicione ao menos um item.");
    if (itens.some(i => !i.descricao)) return alert("Todo item precisa de descrição.");
    setSalvando(true);
    const payload = {
      ...f,
      numero: (f.numero || "").trim(),
      dataEmissao: f.dataEmissao ? (String(f.dataEmissao).length === 10 ? f.dataEmissao + "T12:00:00" : f.dataEmissao) : undefined,
      descontoGeral: Number(f.descontoGeral) || 0,
      validade: f.validade || null,
      dataAprovacao: f.dataAprovacao || null,
      itens: itens.map(i => ({
        tipo: i.tipo, descricao: i.descricao, ncm: i.ncm || "", unidade: i.unidade || "un",
        quantidade: Number(i.quantidade) || 0,
        valorUnitario: Number(i.valorUnitario) || 0,
        desconto: Number(i.desconto) || 0,
      })),
    };
    try {
      if (dados._id) await api.put(`/orcamentos/${dados._id}`, payload);
      else await api.post("/orcamentos", payload);
      if (f.responsavelFinanceiro && f.responsavelFinanceiro !== (dados.responsavelFinanceiro || "")) {
        try { await api.post("/notificacoes", { tipo:"alocacao", titulo:"Você foi alocado(a) como responsável financeiro", texto:((f.numero||"Orçamento")+" · "+(f.nomeCliente||"")).trim(), para:[f.responsavelFinanceiro] }); } catch(_e){}
      }
      onSalvo();
    } catch (e) {
      console.error(e);
      alert("Erro ao salvar: " + (e?.response?.data?.erro || e.message));
    }
    setSalvando(false);
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(16,24,40,.55)", display:"flex", alignItems:"flex-start", justifyContent:"center", zIndex:1000, padding:"30px 16px", overflow:"auto" }}>
      <div onClick={e => e.stopPropagation()} style={{ background:"var(--card-bg)", borderRadius:12, width:"100%", maxWidth:1360, padding:26, boxShadow:"0 20px 60px rgba(0,0,0,.25)" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:18 }}>
          <h2 style={{ margin:0, fontSize:19, fontWeight:800, color:"var(--text-primary)" }}>
            {dados._id ? `Editar ${dados.numero || ""}` : "Novo orçamento"}
          </h2>
          <button onClick={onFechar} style={{ border:"none", background:"none", fontSize:22, color:"#98A2B3", cursor:"pointer", lineHeight:1 }}>×</button>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"repeat(6, 1fr)", gap:12, marginBottom:18 }}>
          <div>
            <label style={lbl}>Número</label>
            <input
              style={{ ...inp, fontWeight:700, color:"#EE6A1A", fontFamily:"monospace" }}
              value={f.numero}
              onChange={e => set("numero", e.target.value)}
              placeholder={dados._id ? "" : "Automático"}
            />
          </div>

          <div style={{ gridColumn:"span 3" }}>
            <label style={lbl}>{t("Cliente *")}</label>
            <div style={{ display:"flex", gap:6, alignItems:"flex-start" }}>
              <div style={{ flex:1 }}>
            <Combo
              opcoes={clientes.map(c => ({ id:c._id, label:(c.nomeEmpresa || c.nomeContato || "—") }))}
              valor={f.cliente}
              placeholder={t("Digite para buscar o cliente...")}
              vazio="Nenhum"
              onChange={(id) => {
                const c = clientes.find(x => x._id === id);
                set("cliente", id);
                set("nomeCliente", c ? (c.nomeEmpresa || c.nomeContato || "") : "");
                set("negociacao", "");
                set("nomeNegociacao", "");
              }}
            />
              </div>
              <Btn onClick={() => setClienteRapido(true)} cor="#1D9E75" full>+ Novo</Btn>
            </div>
          </div>

          <div style={{ gridColumn:"span 2" }}>
            <label style={lbl}>{t("Vendedor *")}</label>
            <Combo
              opcoes={usuarios.map(u => ({ id:u._id, label:u.nome }))}
              valor={f.responsavel}
              placeholder={t("Buscar vendedor...")}
              vazio="Nenhum"
              onChange={(id) => {
                const u = usuarios.find(x => x._id === id);
                set("responsavel", id);
                set("nomeResponsavel", u ? u.nome : "");
              }}
            />
          </div>

          <div style={{ gridColumn:"span 2" }}>
            <label style={lbl}>{t("Responsável financeiro")}</label>
            <Combo
              opcoes={usuarios.map(u => ({ id:u._id, label:u.nome }))}
              valor={f.responsavelFinanceiro}
              placeholder={t("Buscar responsável...")}
              vazio="Nenhum"
              onChange={(id) => {
                const u = usuarios.find(x => x._id === id);
                set("responsavelFinanceiro", id);
                set("nomeResponsavelFinanceiro", u ? u.nome : "");
              }}
            />
          </div>

          <div style={{ gridColumn:"span 2" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", height:16, marginBottom:4 }}>
              <span style={{ ...lbl, marginBottom:0 }}>{t("Negociação / Serviço")}</span>
              {f.cliente && (
                <label style={{ fontSize:10, color:"#98A2B3", display:"flex", alignItems:"center", gap:3, cursor:"pointer" }}>
                  <input type="checkbox" checked={todasNegocs} onChange={e => setTodasNegocs(e.target.checked)} style={{ margin:0 }} />
                  todas
                </label>
              )}
            </div>
            <Combo
              opcoes={negocsDoCliente.map(n => ({ id: n._id, label: rotuloNegoc(n) }))}
              valor={f.negociacao}
              placeholder={f.cliente ? "Buscar serviço/negociação..." : "Selecione o cliente primeiro..."}
              vazio="Nenhuma"
              onChange={(id) => {
                const n = negocs.find(x => x._id === id);
                set("negociacao", id);
                set("nomeNegociacao", n ? ((n.servico || "").trim() || n.nome || n.empresa || "") : "");
              }}
            />
          </div>

          <div style={{ gridColumn:"span 2" }}>
            <label style={lbl}>Assunto</label>
            <input style={inp} value={f.titulo} onChange={e => set("titulo", e.target.value)} placeholder={t("Ex: CFTV Perimetral")} />
          </div>

          <div>
            <label style={lbl}>Status</label>
            <select style={inp} value={f.status} onChange={e => set("status", e.target.value)}>
              {Object.keys(ST).map(k => <option key={k} value={k}>{ST[k].t}</option>)}
            </select>
          </div>

          <div>
            <label style={lbl}>Prioridade</label>
            <select style={inp} value={f.prioridade} onChange={e => set("prioridade", e.target.value)}>
              {Object.keys(PRI).map(k => <option key={k} value={k}>{PRI[k].t}</option>)}
            </select>
          </div>

          <div>
            <label style={lbl}>Data de emissão</label>
            <div style={{ display:"flex", gap:6 }}>
              <input type="date" style={inp} value={f.dataEmissao} onChange={e => set("dataEmissao", e.target.value)} />
              <button type="button" onClick={() => set("dataEmissao", new Date().toISOString().slice(0,10))} style={{ padding:"0 12px", fontSize:12, fontWeight:600, color:"#EE6A1A", background:"var(--card-bg)", border:"1px solid #EE6A1A", borderRadius:6, cursor:"pointer", whiteSpace:"nowrap" }}>Hoje</button>
            </div>
          </div>
          <div>
            <label style={lbl}>Validade</label>
            <input type="date" style={inp} value={f.validade} onChange={e => set("validade", e.target.value)} />
          </div>

          <div>
            <label style={lbl}>Data de aprovação</label>
            <div style={{ display:"flex", gap:6 }}>
              <input type="date" style={inp} value={f.dataAprovacao} onChange={e => set("dataAprovacao", e.target.value)} />
              <button type="button" onClick={() => set("dataAprovacao", new Date().toISOString().slice(0,10))} style={{ padding:"0 12px", fontSize:12, fontWeight:600, color:"#EE6A1A", background:"var(--card-bg)", border:"1px solid #EE6A1A", borderRadius:6, cursor:"pointer", whiteSpace:"nowrap" }}>Hoje</button>
            </div>
          </div>
        </div>

        <SecaoItens
          titulo="Produtos"
          tipo="produto"
          cor="#579bfc"
          itens={itens}
          catalogo={catalogo}
          onAddCatalogo={addDoCatalogo}
          onAddAvulso={() => addItem({ tipo:"produto", descricao:"", ncm:"", unidade:"un", quantidade:1, valorUnitario:0, desconto:0 })}
          onCadastrar={() => setCadastroRapido("produto")}
          setItem={setItem}
          delItem={delItem}
        />

        <SecaoItens
          titulo="Serviços"
          tipo="servico"
          cor="#a25ddc"
          itens={itens}
          catalogo={catalogo}
          onAddCatalogo={addDoCatalogo}
          onAddAvulso={() => addItem({ tipo:"servico", descricao:"", ncm:"", unidade:"un", quantidade:1, valorUnitario:0, desconto:0 })}
          onCadastrar={() => setCadastroRapido("servico")}
          setItem={setItem}
          delItem={delItem}
        />

        {clienteRapido && (
          <ModalClienteRapido
            onFechar={() => setClienteRapido(false)}
            onCriado={(c) => {
              onClienteCriado(c);
              set("cliente", c._id);
              set("nomeCliente", c.nomeEmpresa || c.nomeContato || "");
              set("negociacao", "");
              set("nomeNegociacao", "");
              setClienteRapido(false);
            }}
          />
        )}

        {cadastroRapido && (
          <ModalCadastroRapido
            tipo={cadastroRapido}
            cor={cadastroRapido === "produto" ? "#579bfc" : "#a25ddc"}
            onFechar={() => setCadastroRapido(null)}
            onCriado={(novo) => {
              addItem({
                tipo: novo.tipo,
                descricao: novo.nome,
                ncm: novo.ncm || "",
                unidade: novo.unidade || "un",
                quantidade: 1,
                valorUnitario: novo.preco || 0,
                desconto: 0,
              });
              if (onCatalogoMudou) onCatalogoMudou();
              setCadastroRapido(null);
            }}
          />
        )}

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 260px", gap:12, marginBottom:14 }}>
          <div>
            <label style={lbl}>Condições de pagamento</label>
            <input style={inp} value={f.condicoesPagamento} onChange={e => set("condicoesPagamento", e.target.value)} placeholder={t("Ex: 50% entrada, 50% na entrega")} />
          </div>
          <div>
            <label style={lbl}>{t("Prazo de entrega")}</label>
            <input style={inp} value={f.prazoEntrega} onChange={e => set("prazoEntrega", e.target.value)} placeholder={t("Ex: 15 dias úteis")} />
          </div>
          <div style={{ background:"var(--page-bg)", border:"1px solid var(--border-color)", borderRadius:8, padding:12 }}>
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:"#579bfc", marginBottom:4 }}>
              <span>Produtos</span><strong>{fmt(totProdutos)}</strong>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:"#a25ddc", marginBottom:6 }}>
              <span>{t("Serviços")}</span><strong>{fmt(totServicos)}</strong>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:"var(--text-secondary)", marginBottom:6, borderTop:"1px solid var(--border-color)", paddingTop:6 }}>
              <span>Subtotal</span><strong>{fmt(subtotal)}</strong>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
              <span style={{ fontSize:12, color:"var(--text-secondary)" }}>Desconto geral</span>
              <input type="number" min="0" step="any" style={{ ...inp, width:100, textAlign:"right" }} value={f.descontoGeral} onChange={e => set("descontoGeral", e.target.value)} />
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", borderTop:"1px solid var(--border-color)", paddingTop:8 }}>
              <strong style={{ fontSize:13, color:"var(--text-primary)" }}>TOTAL</strong>
              <strong style={{ fontSize:16, color:"#1D9E75" }}>{fmt(total)}</strong>
            </div>
          </div>
        </div>

        <div style={{ marginBottom:18 }}>
          <label style={lbl}>Observações</label>
          <textarea style={{ ...inp, minHeight:60, resize:"vertical" }} value={f.observacoes} onChange={e => set("observacoes", e.target.value)} />
        </div>

        <div style={{ marginBottom:18 }}>
          <AnexosBox tipo="orcamento" id={dados._id} anexos={dados.anexos || []} />
        </div>

        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:10 }}>
          {dados._id ? <Btn onClick={async () => { await onExcluir(dados._id); onFechar(); }} cor="#E24B4A">Excluir</Btn> : <span />}
          <div style={{ display:"flex", gap:10 }}>
            {dados._id && dados.status === "aprovado" && (currentUser?.email||"").toLowerCase() === "adrianoaquino@optimustecnologia.net" && <Btn onClick={async () => { if(!window.confirm("Gerar comissão deste orçamento para " + (dados.nomeResponsavel||"o vendedor") + "?")) return; try { const r = await api.post("/comissoes/do-orcamento/" + dados._id); alert("Comissão gerada para " + (r.data?.vendedor||"vendedor") + " — serviços R$ " + Number(r.data?.valorServicos||0).toLocaleString("pt-BR")); } catch(e){ alert(e?.response?.data?.erro || e?.response?.data?.message || "Erro ao gerar comissão."); } }} cor="#1D9E75">Gerar comissão</Btn>}
            <Btn onClick={onFechar} cor="#98A2B3">Cancelar</Btn>
            <Btn onClick={salvar} cor="#EE6A1A" full>{salvando ? "Salvando..." : "Salvar orçamento"}</Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

